<template>
  <div class="telematics-consent-form">
    <div class="pa-4 text-center">
      <img src="~@images/logo.svg" class="logo" alt="">

      <!-- OAuth provider return (?code&state): the provider redirected the driver back here → exchange + capture
           on the consent, then resume the tunnel. Spinner / multi-vehicle pick / error. -->
      <div v-if="oauthReturn" class="py-8">
        <div v-if="oauthError">
          <v-icon size="64" color="error" class="mb-4">mdi-alert-circle</v-icon>
          <div class="title mb-2">{{ $t('telematics-consent.oauth-error') }}</div>
          <v-alert type="error" outlined class="mt-4 mx-auto" style="max-width:480px">{{ oauthError }}</v-alert>
        </div>
        <div v-else-if="oauthSelect" class="mx-auto text-left" style="max-width:480px">
          <div class="title mb-4 text-center">{{ $t('telematics-consent.oauth-select-vehicle') }}</div>
          <v-list>
            <v-list-item
                v-for="v in oauthVehicles" :key="v.externalVehicleId"
                :disabled="!!oauthSelectedId" @click="selectOAuthVehicle(v)"
            >
              <v-list-item-icon><v-icon>mdi-car</v-icon></v-list-item-icon>
              <v-list-item-content>
                <v-list-item-title>{{ v.make }} {{ v.model }}</v-list-item-title>
                <v-list-item-subtitle v-if="v.year">{{ v.year }}</v-list-item-subtitle>
              </v-list-item-content>
              <v-list-item-action v-if="oauthSelectedId === v.externalVehicleId">
                <v-progress-circular indeterminate size="20" width="2" color="primary" />
              </v-list-item-action>
            </v-list-item>
          </v-list>
        </div>
        <div v-else>
          <v-progress-circular indeterminate size="64" color="primary" class="mb-4" />
          <div class="title">{{ $t('telematics-consent.oauth-processing') }}</div>
        </div>
      </div>

      <!-- Non-form states: loading / thank-you / already granted / invalid token / "not you" abort.
           Email/app drivers have NO portal account — the flow ENDS here on a clean closing page, it never
           navigates to the portal (which would demand a login). -->
      <div v-else-if="!loaded || sendSuccess || alreadyGranted || !tokenIsValid || notYou">
        <!-- Thank-you (consent just granted) / already granted: a proper closing screen -->
        <v-card v-if="loaded && (sendSuccess || alreadyGranted)" max-width="560" class="mx-auto tc-thanks">
          <div class="tc-thanks__badge">
            <v-icon size="52" color="white">mdi-check</v-icon>
          </div>
          <h1 class="tc-thanks__title">{{ $t('telematics-consent.thanks-title') }}</h1>
          <p class="tc-thanks__text">
            {{ sendSuccess ? $t('telematics-consent.thanks-text', { vehicle: vehicleLabel }) : $t('telematics-consent.already-granted') }}
          </p>
          <p class="tc-thanks__sub">{{ $t('telematics-consent.thanks-sub') }}</p>
        </v-card>

        <!-- "Ce n'est pas vous ?" — the driver says the identity isn't theirs. We abort (no data is changed) and
             tell them to have their fleet manager fix it; nothing is sent automatically. -->
        <v-card v-else-if="loaded && notYou" max-width="560" class="mx-auto tc-thanks tc-thanks--muted">
          <div class="tc-thanks__badge tc-thanks__badge--muted">
            <v-icon size="52" color="white">mdi-account-alert</v-icon>
          </div>
          <h1 class="tc-thanks__title">{{ $t('telematics-consent.not-you-title') }}</h1>
          <p class="tc-thanks__text">{{ $t('telematics-consent.not-you-text') }}</p>
        </v-card>

        <!-- Invalid / expired link -->
        <v-card v-else-if="loaded && !tokenIsValid" max-width="560" class="mx-auto tc-thanks tc-thanks--muted">
          <div class="tc-thanks__badge tc-thanks__badge--muted">
            <v-icon size="52" color="white">mdi-link-off</v-icon>
          </div>
          <h1 class="tc-thanks__title">{{ $t('telematics-consent.invalid-title') }}</h1>
          <p class="tc-thanks__text">{{ $t('invalid-token') }}</p>
        </v-card>

        <!-- Initial loading -->
        <div class="car" v-else>
          <img src="~@images/car.front.png" />
        </div>
      </div>

      <!-- Consent tunnel -->
      <div v-else>
        <v-card max-width="880" class="mx-auto text-left tc-card">
          <headline bg>{{ $t('telematics-consent.title') }}</headline>

          <div class="px-6 py-4">
            <!-- The pre-created vehicle (never re-asked) — shown on every screen -->
            <div class="tc-vehicle mb-5">
              <v-icon size="34" color="primary" class="mr-3">mdi-car</v-icon>
              <div>
                <div class="tc-vehicle__name">{{ vehicleLabel }}</div>
                <div class="tc-vehicle__sub" v-if="consent.vehicleRegistration">{{ consent.vehicleRegistration }}</div>
              </div>
            </div>

            <!-- ── Step banners (full-width, vertical) ── -->
            <div class="tc-steps mb-5">
              <div
                  v-for="s in steps" :key="s.n"
                  class="tc-step-banner"
                  :class="{ 'tc-step-banner--current': step === s.n, 'tc-step-banner--done': step > s.n, 'tc-step-banner--clickable': step > s.n }"
                  @click="step > s.n && (step = s.n)"
              >
                <div class="tc-step-banner__badge">
                  <v-icon v-if="step > s.n" small color="white">mdi-check</v-icon>
                  <span v-else>{{ s.n }}</span>
                </div>
                <div class="tc-step-banner__title">{{ s.title }}</div>
                <v-icon v-if="step > s.n" small color="success" class="tc-step-banner__state">mdi-check-circle</v-icon>
              </div>
            </div>

            <!-- ── STEP 1 — Identité ── -->
            <div v-if="step === 1">
              <p class="mb-1">{{ $t('telematics-consent.intro', { vehicle: vehicleLabel }) }}</p>
              <p class="tc-step-lead">{{ $t('telematics-consent.step-identity-lead') }}</p>

              <div class="tc-identity">
                <v-row>
                  <v-col cols="12" md="6">
                    <div class="tc-identity__label">{{ $t('firstname') }}</div>
                    <div class="tc-identity__value">{{ form.firstname || '—' }}</div>
                  </v-col>
                  <v-col cols="12" md="6">
                    <div class="tc-identity__label">{{ $t('lastname') }}</div>
                    <div class="tc-identity__value">{{ form.lastname || '—' }}</div>
                  </v-col>
                </v-row>
              </div>

              <div class="d-flex justify-space-between align-center mt-6">
                <v-btn text @click="notYou = true">
                  <v-icon left small>mdi-account-question-outline</v-icon>{{ $t('telematics-consent.not-you') }}
                </v-btn>
                <v-btn color="primary" large @click="step = 2">
                  {{ $t('next') }}<v-icon right>mdi-arrow-right</v-icon>
                </v-btn>
              </div>
            </div>

            <!-- ── STEP 2 — Accès au véhicule ── -->
            <div v-else-if="step === 2">
              <v-form ref="form2" @submit.prevent="isMeter ? goHomeStep() : submit()">
                <p class="tc-step-lead">{{ $t('telematics-consent.step-vehicle-lead') }}</p>

                <!-- OAuth (SmartCar): connect the OEM account here; captured & persisted for the final grant -->
                <template v-if="consent.type === 'oauth'">
                  <v-alert v-if="consent.oauthCaptured" type="success" text dense class="mb-4">
                    <v-icon left small color="success">mdi-check-circle</v-icon>
                    {{ $t('telematics-consent.oauth-connected', { vehicle: consent.oauthVehicle || vehicleLabel }) }}
                  </v-alert>
                  <template v-else>
                    <p class="text-caption grey--text text--darken-1 mb-3">{{ $t('telematics-consent.oauth-hint') }}</p>
                    <v-btn color="primary" outlined block large :loading="connecting" @click="connectOAuth">
                      <v-icon left>mdi-link-variant</v-icon>
                      {{ $t('telematics-consent.oauth-connect') }}
                    </v-btn>
                  </template>
                </template>

                <!-- VIN: shown for confirmation/correction -->
                <template v-else>
                  <p class="text-caption grey--text text--darken-1 mb-1">{{ $t('telematics-consent.vin-hint') }}</p>
                  <v-text-field
                      v-model="form.vin" :label="$t('vehicle.vin')"
                      @input="form.vin = (form.vin || '').toUpperCase()"
                      counter="17" maxlength="17" outlined dense :rules="[vinRule]"
                  />
                </template>

                <!-- Consent acceptance for the vehicle data (folds in the charge geolocation in meter mode) -->
                <div class="tc-consent mt-2" :class="{ 'tc-consent--on': accept.vehicle }" @click="accept.vehicle = !accept.vehicle">
                  <div class="tc-consent__body">
                    <div class="tc-consent__title">{{ $t('telematics-consent.block-vehicle-title') }}</div>
                    <div class="tc-consent__text">{{ vehicleConsentText }}</div>
                  </div>
                  <v-simple-checkbox :value="accept.vehicle" @input="accept.vehicle = $event" :ripple="false" color="primary" />
                </div>

                <div class="d-flex justify-space-between align-center mt-6">
                  <v-btn text @click="step = 1"><v-icon left>mdi-arrow-left</v-icon>{{ $t('back') }}</v-btn>
                  <v-btn color="primary" large :disabled="!canSubmitVehicle" type="submit">
                    {{ isMeter ? $t('next') : $t('telematics-consent.grant') }}
                    <v-icon right v-if="isMeter">mdi-arrow-right</v-icon>
                  </v-btn>
                </div>
              </v-form>
            </div>

            <!-- ── STEP 3 — Domicile (meter only) ── -->
            <div v-else-if="step === 3 && isMeter">
              <v-form ref="form3" @submit.prevent="submit">
                <p class="tc-step-lead">{{ $t('telematics-consent.step-home-lead') }}</p>

                <!-- Location: reuse the address on file OR search another one. The search field only appears
                     when the driver opts for a different location. -->
                <v-radio-group v-if="memberAddress" v-model="homeChoice" class="tc-home-choice mt-0" hide-details>
                  <v-radio value="existing" color="primary">
                    <template #label>
                      <div>
                        <div class="tc-home-choice__title">{{ $t('telematics-consent.home-choice-existing') }}</div>
                        <div class="tc-home-choice__addr">{{ memberAddressLabel }}</div>
                      </div>
                    </template>
                  </v-radio>
                  <v-radio value="new" color="primary" :label="$t('telematics-consent.home-choice-new')" />
                </v-radio-group>

                <v-autocomplete
                    v-if="homeChoice === 'new' || !memberAddress"
                    v-model="selectedPlaceId"
                    :items="addressItems"
                    :search-input.sync="addressSearch"
                    :loading="addressLoading"
                    item-text="label"
                    item-value="placeId"
                    :label="$t('telematics-consent.address-search')"
                    prepend-inner-icon="mdi-map-search-outline"
                    outlined dense clearable no-filter
                    hide-no-data return-object
                    class="mt-3"
                    :rules="[() => hasAddress || $t('telematics-consent.address-required')]"
                    @change="onAddressSelected"
                />
                <div v-if="homeChoice === 'new' && hasAddress" class="tc-address-picked">
                  <v-icon small color="success" class="mr-1">mdi-map-marker-check</v-icon>
                  {{ form.addressStreet }}, {{ form.addressZipcode }} {{ form.addressCity }} — {{ form.addressCountry }}
                </div>

                <!-- Energy bill — France only -->
                <template v-if="billRequired">
                  <headline class="mt-4">{{ $t('telematics-consent.bill-title') }}</headline>
                  <p class="text-caption grey--text text--darken-1 mb-2">{{ $t('telematics-consent.bill-hint') }}</p>
                  <v-file-input
                      v-model="form.electricityBill"
                      :label="$t('telematics-consent.electricity-bill')"
                      accept="image/*,application/pdf"
                      show-size append-icon="mdi-paperclip" outlined dense
                      :rules="[commonRules.required]"
                  />
                </template>

                <div class="d-flex justify-space-between align-center mt-6">
                  <v-btn text @click="step = 2"><v-icon left>mdi-arrow-left</v-icon>{{ $t('back') }}</v-btn>
                  <v-btn color="primary" large :disabled="!canGrant" type="submit">
                    {{ $t('telematics-consent.grant') }}
                  </v-btn>
                </div>
              </v-form>
            </div>
          </div>
        </v-card>
      </div>
    </div>
  </div>
</template>

<script>
import VehicleRepository from "@repository/VehicleRepository";
import LocationRepository from "@repository/LocationRepository";
import RulesMixin from "@mixins/RulesMixin";
import { v4 as uuidv4 } from 'uuid';

export default {
  name: 'TelematicsConsentForm',
  mixins: [RulesMixin],
  data() {
    return {
      token: null,
      consent: null,
      tokenIsValid: false,
      loaded: false,
      sendSuccess: false,
      alreadyGranted: false,
      notYou: false,
      connecting: false,
      // OAuth provider return (?code&state) — exchange + capture, with a multi-vehicle pick when needed.
      oauthReturn: false,
      oauthSelect: false,
      oauthVehicles: [],
      oauthSelectedId: null,
      oauthError: null,
      step: 1, // 1 = identity, 2 = vehicle, 3 = home (meter only)
      accept: { vehicle: false },
      // home location: reuse the member's address on file ('existing') or search another one ('new')
      homeChoice: 'existing',
      memberAddress: null, // the member's address on file (snapshot, restored when choosing 'existing')
      // address autocomplete
      addressSearch: null,
      addressItems: [],
      addressLoading: false,
      selectedPlaceId: null,
      addressSessionToken: null,
      form: {
        firstname: null,
        lastname: null,
        vin: null,
        addressStreet: null,
        addressZipcode: null,
        addressCity: null,
        addressCountry: null,
        electricityBill: null,
      },
    }
  },

  computed: {
    // Meter (reimbursement) mode has a home step; telemetry stops at the vehicle step.
    isMeter() {
      return this.consent?.mode === 'meter' || !!this.consent?.includesChargeSessions
    },
    steps() {
      const s = [
        { n: 1, title: this.$t('telematics-consent.step-identity') },
        { n: 2, title: this.$t('telematics-consent.step-vehicle') },
      ]
      if (this.isMeter) s.push({ n: 3, title: this.$t('telematics-consent.step-home') })
      return s
    },
    vehicleLabel() {
      const c = this.consent || {}
      const brandModel = `${c.vehicleBrand || ''} ${c.vehicleModel || ''}`.trim()
      return brandModel || c.vehicleName || this.$t('telematics-consent.your-vehicle')
    },
    vehicleConsentText() {
      return this.isMeter
          ? this.$t('telematics-consent.block-vehicle-meter')
          : (this.consent?.type === 'oauth'
              ? this.$t('telematics-consent.block-vehicle-oauth')
              : this.$t('telematics-consent.block-vehicle-vin'))
    },
    vinValid() {
      return /^[A-HJ-NPR-Z0-9]{17}$/.test(this.form.vin || '')
    },
    memberAddressLabel() {
      const a = this.memberAddress
      if (!a) return ''
      return [a.street, [a.zipcode, a.city].filter(Boolean).join(' '), a.country].filter(Boolean).join(', ')
    },
    hasAddress() {
      return !!(this.form.addressStreet && this.form.addressCity)
    },
    // The energy bill is only requested when the home is in France.
    billRequired() {
      const c = (this.form.addressCountry || '').trim().toLowerCase()
      return this.isMeter && (c === 'france' || c === 'fr')
    },
    canSubmitVehicle() {
      if (!this.accept.vehicle) return false
      return this.consent?.type === 'oauth' ? !!this.consent.oauthCaptured : this.vinValid
    },
    canGrant() {
      if (!this.canSubmitVehicle) return false
      if (!this.isMeter) return true
      if (!this.hasAddress) return false
      if (this.billRequired && !this.form.electricityBill) return false
      return true
    },
  },

  watch: {
    addressSearch(query) {
      this.searchAddress(query)
    },
    // Switching the home-location choice: 'existing' restores the address on file, 'new' clears it so the
    // driver must pick one from the search.
    homeChoice(choice) {
      if (choice === 'existing') {
        this.applyMemberAddress()
        this.selectedPlaceId = null
        this.addressItems = []
      } else {
        this.resetAddress()
        this.selectedPlaceId = null
      }
    },
  },

  mounted() {
    this.addressSessionToken = uuidv4()
    // The OAuth provider returns the driver here — handle that before the normal load.
    // SmartCar: ?code&state. Enode: ?token&oauth_return=enode (no code — the server queries the linked user).
    const q = this.$route.query
    if (q.code && q.state) {
      this.oauthReturn = true
      this.handleOAuthReturn({ code: q.code, state: q.state })
    } else if (q.oauth_return === 'enode' && q.token) {
      this.oauthReturn = true
      this.handleOAuthReturn({ token: decodeURIComponent(q.token) })
    } else {
      this.loadConsent()
    }
  },

  methods: {
    vinRule(v) {
      if (!v) return this.$t('required')
      return /^[A-HJ-NPR-Z0-9]{17}$/.test(v) || this.$t('vehicle.wizard.vin-invalid')
    },

    loadConsent(token = decodeURIComponent(this.$route.query.token || '')) {
      this.token = token
      this.loaded = false
      this.showLoading(true)

      if (!token) {
        this.tokenIsValid = false
        this.loaded = true
        this.showLoading(false)
        return
      }

      VehicleRepository.getTelematicsConsent(token).then(res => {
        this.consent = res
        this.tokenIsValid = true
        this.alreadyGranted = res.status === 'GRANTED' || res.status === 'granted'
        // Pre-fill from the selected member's record; edits update the member on grant.
        this.form.firstname = res.firstname || null
        this.form.lastname = res.lastname || null
        this.form.vin = res.vin || null
        if (res.address && (res.address.street || res.address.city)) {
          this.memberAddress = res.address
          this.homeChoice = 'existing'
          this.applyMemberAddress()
        } else {
          this.memberAddress = null
          this.homeChoice = 'new'
        }
        // Returning from SmartCar: the OEM account is connected → identity + vehicle are done, jump to the home
        // step (meter) or straight to the ready-to-grant vehicle step (telemetry).
        if (res.type === 'oauth' && res.oauthCaptured) {
          this.accept.vehicle = true
          this.step = this.isMeter ? 3 : 2
        }
        this.loaded = true
        this.showLoading(false)
      }).catch(e => {
        this.tokenIsValid = false
        this.loaded = true
        this.showLoading(false)
        this.notifyError(e)
      })
    },

    applyMemberAddress() {
      const a = this.memberAddress
      if (!a) return
      this.form.addressStreet = a.street || null
      this.form.addressZipcode = a.zipcode || null
      this.form.addressCity = a.city || null
      this.form.addressCountry = a.country || null
    },

    connectOAuth() {
      this.connecting = true
      VehicleRepository.telematicsConsentOAuthUrl(this.token, this.consent.vehicleBrand || null)
          .then(res => {
            if (res && res.url) {
              window.location.href = res.url
            } else {
              this.connecting = false
            }
          })
          .catch(e => {
            this.connecting = false
            this.notifyError(e)
          })
    },

    // The provider redirected the driver back → exchange (SmartCar) / query (Enode) + capture on the consent.
    handleOAuthReturn(payload) {
      VehicleRepository.telematicsConsentOAuthCallback(payload)
          .then(data => this.onOAuthResult(data))
          .catch(err => this.onOAuthError(err))
    },

    onOAuthResult(data) {
      if (data && data.error === 'AUTHORIZATION_EXPIRED') {
        this.oauthError = this.$t('telematics-consent.oauth-error-expired')
        return
      }
      if (data && data.selectVehicle) {
        this.oauthVehicles = data.vehicles || []
        this.oauthSelect = true
        return
      }
      if (data && data.captured) {
        this.resumeAfterCapture()
        return
      }
      this.oauthError = this.$t('telematics-consent.oauth-error')
    },

    selectOAuthVehicle(vehicle) {
      if (this.oauthSelectedId) return
      this.oauthSelectedId = vehicle.externalVehicleId
      // Only the consent token (from the OAuth state) + the chosen id — the server owns the tokens and the list.
      VehicleRepository.telematicsConsentOAuthSelect(this.returnConsentToken(), vehicle.externalVehicleId)
          .then(data => this.onOAuthResult(data))
          .catch(err => {
            this.oauthSelectedId = null
            this.onOAuthError(err)
          })
    },

    // Capture done → drop the ?code&state from the URL and resume the tunnel with the vehicle step connected.
    resumeAfterCapture() {
      const token = this.returnConsentToken()
      this.$router.replace({ name: 'telematics.consent', query: { token } }).catch(() => {})
      this.oauthReturn = false
      this.oauthSelect = false
      this.loadConsent(token)
    },

    // The consent token behind the current OAuth return: Enode carries it as ?token, SmartCar inside the state.
    returnConsentToken() {
      const q = this.$route.query
      if (q.token) {
        return decodeURIComponent(q.token)
      }
      try {
        return JSON.parse(atob(q.state)).consentToken || null
      } catch (e) {
        return null
      }
    },

    onOAuthError(err) {
      const data = err && err.response ? err.response.data : null
      this.oauthError = data && data.error === 'AUTHORIZATION_EXPIRED'
          ? this.$t('telematics-consent.oauth-error-expired')
          : ((data && data.message) || this.$t('telematics-consent.oauth-error'))
    },

    goHomeStep() {
      if (!this.canSubmitVehicle) return
      this.step = 3
    },

    searchAddress(query) {
      if (this.selectedPlaceId || !query || query.length < 3) {
        return
      }
      this.addressLoading = true
      // /address/search returns [{ placeId, address: <main line>, location: <secondary line> }].
      LocationRepository.searchAddress(query, this.addressSessionToken)
          .then(res => {
            this.addressItems = (res || []).map(r => ({
              placeId: r.placeId,
              label: [r.address, r.location].filter(Boolean).join(', '),
            }))
          })
          .catch(() => { this.addressItems = [] })
          .finally(() => { this.addressLoading = false })
    },

    onAddressSelected(item) {
      if (!item || !item.placeId) {
        this.resetAddress()
        return
      }
      // /address/place returns { address, coordinates, detail: { number, street, city, zipcode, country } }.
      LocationRepository.getPlace(item.placeId, this.addressSessionToken).then(place => {
        const d = (place && place.detail) || {}
        this.form.addressStreet = [d.number, d.street].filter(Boolean).join(' ') || (place && place.address) || null
        this.form.addressZipcode = d.zipcode || null
        this.form.addressCity = d.city || null
        this.form.addressCountry = d.country || null
      }).catch(() => {})
    },

    resetAddress() {
      this.form.addressStreet = null
      this.form.addressZipcode = null
      this.form.addressCity = null
      this.form.addressCountry = null
    },

    submit() {
      if (!this.canGrant) return
      if (this.isMeter && this.$refs.form3 && !this.$refs.form3.validate()) return
      this.showLoading(true)

      const payload = {
        token: this.token,
        firstname: this.form.firstname,
        lastname: this.form.lastname,
        addressStreet: this.form.addressStreet,
        addressZipcode: this.form.addressZipcode,
        addressCity: this.form.addressCity,
        addressCountry: this.form.addressCountry,
      }
      if (this.consent.type === 'vin' && this.form.vin) payload.vin = this.form.vin

      VehicleRepository.grantTelematicsConsent(payload, this.form.electricityBill).then(() => {
        // OAuth was already captured on the vehicle step → the grant materialises the connection. Reproduce the
        // legacy behaviour: return to wherever the flow was opened from (portal, or the app deep-link), if given.
        this.showLoading(false)
        if (this.consent && this.consent.returnUrl) {
          window.location.href = this.consent.returnUrl
          return
        }
        this.sendSuccess = true
      }).catch(e => {
        this.showLoading(false)
        this.notifyError(e)
      })
    },
  },
}
</script>

<style lang="scss">
.telematics-consent-form {
  .tc-thanks {
    margin-top: 48px;
    padding: 40px 32px 36px;
    border-radius: 18px;
    text-align: center;
    box-shadow: 0 8px 30px rgba(0, 0, 0, 0.06) !important;
    &__badge {
      width: 84px; height: 84px; margin: 0 auto 22px;
      border-radius: 50%;
      display: flex; align-items: center; justify-content: center;
      background: linear-gradient(135deg, #22c55e, #16a34a);
      box-shadow: 0 8px 20px rgba(34, 197, 94, 0.35);
      &--muted { background: linear-gradient(135deg, #9ca3af, #6b7280); box-shadow: none; }
    }
    &__title { font-size: 22px; font-weight: 800; margin-bottom: 12px; color: #1f2937; }
    &__text { font-size: 15px; color: #374151; line-height: 1.55; margin-bottom: 8px; }
    &__sub { font-size: 13px; color: #9ca3af; margin-bottom: 0; }
    &--muted { box-shadow: 0 8px 30px rgba(0, 0, 0, 0.05) !important; }
  }
  .tc-vehicle {
    display: flex; align-items: center;
    background: #f7f8fa; border-radius: 12px; padding: 12px 16px;
    &__name { font-weight: 700; font-size: 15px; }
    &__sub { font-size: 12.5px; color: #777; }
  }
  // Full-width vertical step banners
  .tc-steps { display: flex; flex-direction: column; gap: 10px; }
  .tc-step-banner {
    display: flex; align-items: center; gap: 14px;
    background: #f7f8fa; border: 2px solid transparent; border-radius: 12px; padding: 14px 18px;
    transition: border-color .15s, background .15s;
    &__badge {
      width: 30px; height: 30px; flex: 0 0 30px;
      border-radius: 50%; background: #d0d3d8; color: #fff;
      display: flex; align-items: center; justify-content: center;
      font-weight: 800; font-size: 14px;
    }
    &__title { font-weight: 700; font-size: 15px; color: #6b7280; flex: 1; }
    &__state { margin-left: auto; }
    &--current {
      border-color: var(--v-primary-base, #e53c22); background: #fff5f3;
      .tc-step-banner__badge { background: var(--v-primary-base, #e53c22); }
      .tc-step-banner__title { color: #1f2937; }
    }
    &--done {
      .tc-step-banner__badge { background: #22c55e; }
      .tc-step-banner__title { color: #374151; }
    }
    &--clickable { cursor: pointer; }
  }
  .tc-step-lead { font-size: 13.5px; color: #555; margin-bottom: 16px; }
  .tc-identity {
    background: #f7f8fa; border-radius: 12px; padding: 8px 16px 14px;
    &__label { font-size: 12px; color: #9ca3af; margin-bottom: 2px; margin-top: 8px; }
    &__value { font-weight: 700; font-size: 16px; color: #1f2937; }
  }
  .tc-home-choice {
    &__title { font-weight: 700; font-size: 14px; color: #1f2937; }
    &__addr { font-size: 12.5px; color: #666; }
  }
  .tc-consent {
    display: flex; align-items: center; gap: 14px;
    border: 2px solid #e0e0e0; border-radius: 12px; padding: 14px 16px; cursor: pointer;
    transition: border-color .15s, background .15s;
    &:hover { border-color: #bdbdbd; }
    &--on { border-color: var(--v-primary-base, #e53c22); background: #fff5f3; }
    &__body { flex: 1; min-width: 0; }
    &__title { font-weight: 700; font-size: 14px; margin-bottom: 2px; }
    &__text { font-size: 12.5px; color: #555; line-height: 1.4; }
  }
  .tc-address-picked { font-size: 12.5px; color: #333; margin: 6px 0; }
}
@media (max-width: 600px) {
  .telematics-consent-form {
    height: auto !important;
    .logo { width: 100%; margin-top: 50px; }
  }
}
</style>
